import { useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import SearchInput from '@/components/SearchInput'
import IconButton from '@mui/material/IconButton'
import { alpha } from '@mui/material/styles'
import ChannelsFilterSelect, {
  getEffectiveChannelIds,
  type ChannelId,
} from '@/components/ChannelsFilterSelect'
import { OutlinedInputProps } from '@mui/material/OutlinedInput'

const SEARCH_COLLAPSED_WIDTH = 40
const SEARCH_EXPANDED_WIDTH = 180

const EnteranceEventsFeedToolbar = ({
  channelsList = [],
  onChannelIdsChange,
  onSearchQueryChange,
}) => {
  const { t } = useTranslation()
  const searchInputRef = useRef<OutlinedInputProps['inputRef']>(null)
  const [selectedChannelIds, setSelectedChannelIds] = useState<ChannelId[]>([])
  const [isSearchActive, setIsSearchActive] = useState(false)
  const [hasSearchValue, setHasSearchValue] = useState(false)
  const isSearchExpanded = isSearchActive || hasSearchValue

  const expandAndFocusSearch = () => {
    setIsSearchActive(true)
    searchInputRef.current?.focus()
  }

  const handleChannelsChange = (channelIds: ChannelId[]) => {
    setSelectedChannelIds(channelIds)
    onChannelIdsChange(getEffectiveChannelIds(channelIds, channelsList))
  }

  const handleSearchChange = (value) => {
    setHasSearchValue(Boolean(String(value || '').trim()))
    onSearchQueryChange?.(value)
  }
  
  return (
    <Box
      sx={{ 
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        width: '100%', 
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        onClick={expandAndFocusSearch}
        sx={(theme) => ({
          flexShrink: 0,
          width: isSearchExpanded ? SEARCH_EXPANDED_WIDTH : SEARCH_COLLAPSED_WIDTH,
          overflow: 'hidden',
          cursor: isSearchExpanded ? 'text' : 'pointer',
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeInOut,
          }),
        })}
      >
        <SearchInput 
          placeholder={isSearchExpanded ? t('search') : ''}
          inputProps={{ 'aria-label': t('search') }}
          inputRef={searchInputRef}
          onChange={handleSearchChange}
          onClick={expandAndFocusSearch}
          onFocus={() => setIsSearchActive(true)}
          onBlur={(event) => {
            setIsSearchActive(false)
            setHasSearchValue(Boolean(event.target.value.trim()))
          }}
          size="small"
          sx={(theme) => ({
            width: '100%',
            minWidth: 0,
            maxWidth: 'none',
            flex: 'none',
            justifyContent: isSearchExpanded ? 'flex-start' : 'center',
            px: isSearchExpanded ? undefined : '8.5px',
            '& .MuiInputAdornment-root': {
              marginLeft: isSearchExpanded ? undefined : 0,
              marginRight: isSearchExpanded ? undefined : 0,
            },
            '& .MuiOutlinedInput-input': {
              opacity: isSearchExpanded ? 1 : 0,
              paddingLeft: isSearchExpanded ? undefined : 0,
              paddingRight: isSearchExpanded ? undefined : 0,
              transition: theme.transitions.create(['opacity', 'padding'], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
            },
          })}
        />
      </Box>
      <ChannelsFilterSelect
        channelsList={channelsList}
        value={selectedChannelIds}
        onChange={handleChannelsChange}
        labelId="enterance-events-chat-channels-label"
      />
      {
        isSearchExpanded ? (
          <IconButton
            component={RouterLink}
            nativeButton={false}
            color="primary"
            to="/entries-history"
            variant="outlined"
            sx={(theme) => ({
              ml: 'auto',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.56),
            })}
          >
            <LaunchRoundedIcon 
              sx={{
                width: 18,
                height: 18
              }} 
            />
          </IconButton>
        ) : (
          <Button
            component={RouterLink}
            nativeButton={false}
            to="/entries-history"
            variant="outlined"
            size="small"
            endIcon={<LaunchRoundedIcon />}
            sx={{
              ml: 'auto',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            { t('entries_history') }
          </Button>
        )
      }
    </Box>
  )
}

export default EnteranceEventsFeedToolbar
